import clsx from "clsx";
import { useLayoutEffect, useState, type RefObject } from "react";
import i18n from "../util/i18n.ts";

export const MOBILE_LIST_INITIAL_ITEMS = 6;
const MOBILE_LIST_TOGGLE_RESERVED_HEIGHT = 72;

const getInitialVisibleCount = (itemCount: number) => Math.min(MOBILE_LIST_INITIAL_ITEMS, itemCount);

export const getMeasuredMobileListVisibleCount = ({
	itemBottomOffsets,
	itemCount,
	maxListHeight,
}: {
	itemBottomOffsets: number[];
	itemCount: number;
	maxListHeight: number;
}) => {
	let nextVisibleCount = 0;
	for (const bottom of itemBottomOffsets) {
		if (bottom <= maxListHeight || nextVisibleCount === 0) {
			nextVisibleCount += 1;
		} else {
			break;
		}
	}
	return Math.min(itemCount, Math.max(1, nextVisibleCount));
};

type VisibleCountState = {
	itemCount: number;
	itemSignature: string;
	visibleCount: number;
};

export const useMobileListVisibleCount = ({ expanded, itemCount, itemSignature, listRef, measurementFrozen = false }: { expanded: boolean; itemCount: number; itemSignature: string; listRef: RefObject<HTMLElement | null>; measurementFrozen?: boolean }) => {
	const [visibleCountState, setVisibleCountState] = useState<VisibleCountState>(() => ({
		itemCount,
		itemSignature,
		visibleCount: getInitialVisibleCount(itemCount),
	}));
	const visibleCount = visibleCountState.itemCount === itemCount && visibleCountState.itemSignature === itemSignature ? Math.min(visibleCountState.visibleCount, itemCount) : getInitialVisibleCount(itemCount);

	useLayoutEffect(() => {
		const nextVisibleCount = getInitialVisibleCount(itemCount);
		setVisibleCountState((current) =>
			current.itemCount === itemCount && current.itemSignature === itemSignature && current.visibleCount === nextVisibleCount
				? current
				: {
						itemCount,
						itemSignature,
						visibleCount: nextVisibleCount,
					},
		);
	}, [itemCount, itemSignature]);

	useLayoutEffect(() => {
		if (expanded || itemCount === 0 || measurementFrozen) {
			return;
		}

		const updateVisibleCount = () => {
			const listElement = listRef.current;
			if (!listElement) {
				return;
			}

			const children = Array.from(listElement.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
			if (children.length === 0) {
				return;
			}

			const listTop = listElement.getBoundingClientRect().top;
			const maxListHeight = Math.max(240, window.innerHeight - MOBILE_LIST_TOGGLE_RESERVED_HEIGHT);
			const nextVisibleCount = getMeasuredMobileListVisibleCount({
				itemBottomOffsets: children.map((child) => child.getBoundingClientRect().bottom - listTop),
				itemCount,
				maxListHeight,
			});
			setVisibleCountState((current) =>
				current.itemCount === itemCount && current.itemSignature === itemSignature && current.visibleCount === nextVisibleCount
					? current
					: {
							itemCount,
							itemSignature,
							visibleCount: nextVisibleCount,
						},
			);
		};

		let resizeFrame: number | undefined;
		const scheduleMeasurement = () => {
			if (resizeFrame !== undefined) {
				window.cancelAnimationFrame(resizeFrame);
			}
			resizeFrame = window.requestAnimationFrame(updateVisibleCount);
		};
		const handleResize = () => {
			setVisibleCountState({
				itemCount,
				itemSignature,
				visibleCount: getInitialVisibleCount(itemCount),
			});
			scheduleMeasurement();
		};

		updateVisibleCount();
		window.addEventListener("resize", handleResize);
		const listElement = listRef.current;
		const resizeObserver =
			typeof ResizeObserver === "undefined" || !listElement
				? undefined
				: new ResizeObserver(scheduleMeasurement);
		if (resizeObserver && listElement) {
			resizeObserver.observe(listElement);
		}

		return () => {
			window.removeEventListener("resize", handleResize);
			resizeObserver?.disconnect();
			if (resizeFrame !== undefined) {
				window.cancelAnimationFrame(resizeFrame);
			}
		};
	}, [expanded, itemCount, itemSignature, listRef, measurementFrozen]);

	return {
		canCollapse: visibleCount < itemCount,
		visibleCount,
	};
};

type Props = {
	expanded: boolean;
	onClick: () => void;
};

const MobileListToggle = ({ expanded, onClick }: Props) => {
	return (
		<div className="mobile-list-toggle-wrap">
			<button
				aria-expanded={expanded}
				className={clsx("mobile-list-toggle", {
					"mobile-list-toggle-expanded": expanded,
				})}
				onClick={onClick}
				type="button"
			>
				{expanded
					? i18n.t("dataTable.collapseFields", {
							ns: "ui",
							defaultValue: "Collapse",
						})
					: i18n.t("dataTable.viewAll", {
							ns: "ui",
							defaultValue: "View all",
						})}
			</button>
		</div>
	);
};

export default MobileListToggle;
